<template>
    <component
        :is="stoneRoute ? 'RouterLink' : 'div'"
        v-qa="'stone-item-preview'"
        :to="stoneRoute"
        class="stone-preview"
        :class="classStonePreview"
    >
        <template v-if="tableView">
            <div class="shape-icon-wrapper">
                <img
                    v-if="shapeIcon"
                    class="shape-icon"
                    :alt="shapeIcon.alt"
                    :src="shapeIcon.url"
                />
            </div>

            <div
                class="carat"
                :class="{ 'is-sorted-by': isSortedByCarat }"
            >
                {{ getStoneWeight(stone) }}
            </div>

            <div class="color">{{ getStoneColor(stone) }}</div>

            <div class="clarity">{{ getStoneClarity(stone) }}</div>

            <div class="cut">{{ getStoneCut(stone) }}</div>

            <div class="polish">{{ getStonePolish(stone) }}</div>

            <div class="symmetry">{{ getStoneSymmetry(stone) }}</div>

            <div
                v-if="totalSaleAmount.amount"
                class="price"
                :class="{ 'is-sorted-by': isSortedByPrice }"
            >
                <span v-if="0 === Number(totalSaleAmount.amount)">
                    {{ t('diamond_search.enquire_for_price') }}
                </span>

                <Money :data="totalSaleAmount" />

                <ExpressDeliveryIcon
                    class="express-delivery-icon"
                    v-if="isExpress"
                />
            </div>
        </template>

        <template v-else>
            <div
                class="image-wrapper"
                :class="imageWrapperClasses"
            >
                <img
                    v-if="image && 'url' in image"
                    :alt="imageAlt"
                    :src="image.url"
                    class="image"
                    loading="lazy"
                />

                <Image
                    v-else-if="image && 'image' in image"
                    class="image"
                    loading="lazy"
                    :alt="imageAlt"
                    v-bind="image.image"
                />

                <ExpressDeliveryIcon
                    v-if="isExpress"
                    class="express-delivery-icon"
                />
            </div>

            <div class="details">
                <div class="top-row">
                    <h3 class="title">{{ title }}</h3>

                    <div
                        v-if="totalSaleAmount.amount"
                        class="price"
                    >
                        <span v-if="0 === Number(totalSaleAmount.amount)">
                            {{ t('diamond_search.enquire_for_price') }}
                        </span>

                        <Money :data="totalSaleAmount" />
                    </div>
                </div>

                <div class="subtitle">
                    {{ subtitle }}

                    <div
                        v-if="consultantOnlyInfo.length"
                        v-qa="'consultant-only-info'"
                    >
                        <div
                            v-for="info in consultantOnlyInfo"
                            :key="info"
                        >
                            {{ info }}
                        </div>
                    </div>
                </div>

                <div class="bottom-row">
                    <div
                        v-if="!$slots['button']"
                        class="more-info"
                    >
                        More info +
                    </div>

                    <div
                        v-if="certificate"
                        class="certificate"
                    >
                        {{ certificate?.lab }}
                    </div>

                    <div
                        v-if="$slots['button']"
                        class="button-wrapper"
                    >
                        <slot name="button"></slot>
                    </div>
                </div>
            </div>
        </template>
    </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useTotalSaleAmount } from '@/domain/stone/price'
import { useStoneImage } from '@/domain/stone/useStoneImage'
import { assertNever } from '@/helpers'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import type { RingBuilderStoneTypes } from '@/domain/product/ring/ring-builder'
import {
    getStoneWeight,
    getStoneSubtitle,
    getStoneTitle,
    getStoneColor,
    getStoneClarity,
    getStoneCut,
    getStonePolish,
    getStoneSymmetry,
    getStoneShapeIcon,
    getGemsApiStoneConsultantInfo,
} from './getStoneTitles'

import Money from '../Money.vue'
import { useIsAdmin } from '@/components/app/isAdmin.ts'
import { toCertificate } from '@/domain/stone/certificate'
import Image from '@/components/Image.vue'
import type {
    GemsApi_StoneSorting,
    NonSkuGemstoneSortInput,
    NonSkuLabGrownDiamondSortInput,
} from '@/graphql/api'
import ExpressDeliveryIcon from './search/sku/ExpressDeliveryIcon.vue'

const {
    stone,
    stoneRoute = undefined,
    tableView,
    sorting = undefined,
} = defineProps<{
    stone: RingBuilderStoneTypes
    stoneRoute?: ButtonRouteRelative
    tableView?: boolean
    sorting?:
        | NonSkuLabGrownDiamondSortInput
        | NonSkuGemstoneSortInput
        | GemsApi_StoneSorting
}>()

const { t } = useI18n()
const isAdmin = useIsAdmin()

const image = useStoneImage(computed(() => stone))

const imageAlt = computed(() => {
    let description = ''

    switch (stone.__typename) {
        case 'NonSkuGemstone':
        // fallthrough
        case 'NonSkuLabGrown':
            description = stone.shape.translation.name
            break
        case 'GemsApi_Stone':
            description = `${
                stone.shapeWithInfo.info?.translation.name ||
                stone.shapeWithInfo.code
            } ${
                stone.categoryWithInfo.info?.translation.name ||
                stone.categoryWithInfo.code
            }`
            break
        default:
            assertNever(stone, '')
    }

    return `${description} image`
})

const isSortedByPrice = computed(() => {
    return sorting?.field === 'PRICE'
})

const isSortedByCarat = computed(() => {
    return sorting?.field === 'CARAT' || sorting?.field === 'WEIGHT'
})

const title = computed(() => getStoneTitle(stone))
const subtitle = computed(() => getStoneSubtitle(stone))
const consultantOnlyInfo = computed(() =>
    getGemsApiStoneConsultantInfo(stone, isAdmin.value)
)
const isExpress = computed(() => {
    if (stone.__typename !== 'GemsApi_Stone') {
        return false
    }

    if (stone.deliveryDays === null) {
        return false
    }

    return stone.deliveryDays <= 2
})

const { totalSaleAmount } = useTotalSaleAmount(computed(() => stone))

const classStonePreview = computed(() => ({
    'table-view': tableView,
}))

const shapeIcon = computed(() => getStoneShapeIcon(stone))

const imageWrapperClasses = computed((): Record<string, boolean> => {
    if (!isAdmin.value) {
        return {}
    }

    if (stone.__typename === 'GemsApi_Stone') {
        return {
            red: stone.isB2B || !stone.isValidB2C,
        }
    }

    return {}
})

const stoneWithCertificate = computed(() =>
    stone.__typename === 'GemsApi_Stone' ? stone : null
)
const certificate = toCertificate(stoneWithCertificate)
</script>

<style lang="scss" scoped>
.stone-preview {
    cursor: pointer;

    &.table-view {
        width: 100%;
        display: flex;
        height: 48px;
        align-items: center;
        border-bottom: 1px solid $grey-200;

        font-family: $effra-font-stack;
        font-size: 14px;
        font-weight: 400;
        line-height: 17.5px;
        letter-spacing: 0.05em;
        text-transform: uppercase;

        .shape-icon-wrapper,
        .carat,
        .color,
        .clarity,
        .cut,
        .polish,
        .symmetry,
        .price {
            flex: 0 0 20%;
            display: flex;
            align-items: center;

            &.is-sorted-by {
                font-weight: 600;
                color: var(--blue);
            }

            .express-delivery-icon {
                display: none;

                @include min-width(768) {
                    display: block;
                    margin-left: 5px;
                    font-weight: 400;
                }
            }
        }

        .cut,
        .polish,
        .symmetry {
            display: none;
        }

        .icon-arrow {
            display: none;
        }

        @include min-width(768) {
            .cut,
            .polish,
            .symmetry {
                display: block;
                flex: 0 0 14%;
            }

            .shape-icon-wrapper,
            .carat,
            .color,
            .clarity {
                flex: 0 0 12%;
            }

            .price {
                flex: 0 0 8%;
            }

            .icon-arrow {
                display: block;
            }
        }

        .shape-icon {
            width: 24px;
            height: 24px;
        }
    }

    .image-wrapper {
        position: relative;
        width: 100%;
        aspect-ratio: 1 / 1;

        &.red {
            border: 1px solid $red;
        }

        .image {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .express-delivery-icon {
            position: absolute;
            top: 10px;
            right: 10px;
        }
    }

    .details {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        padding: 12px;
        align-self: flex-end;

        .top-row {
            margin-bottom: 5px;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-around;

            @include min-width(640) {
                margin-bottom: 10px;
            }

            .title {
                font-family: $parcel-font-stack;
                letter-spacing: 0.01em;
                font-size: 1rem;
                line-height: 1em;
                color: $blue;
                width: 100%;

                @include min-width(640) {
                    font-size: 1.125rem;
                }
            }

            .price {
                font-family: $effra-font-stack;
                color: $slate;
                text-transform: uppercase;
                font-size: 15px;
                font-weight: 400;
                line-height: 18.75px;
                letter-spacing: 0.01em;
            }
        }

        .subtitle {
            width: 100%;
            margin-bottom: 20px;
            font-family: $effra-font-stack;

            font-size: 14px;
            font-weight: 400;
            line-height: 17.5px;
            letter-spacing: 0.01em;
            color: $grey-500;

            @include min-width(640) {
                font-size: 0.875rem;
                margin-bottom: 20px;
            }
        }

        .bottom-row {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;

            font-family: $effra-font-stack;
            text-transform: uppercase;
            font-size: 11px;
            font-weight: 400;
            line-height: 17.5px;
            letter-spacing: 0.01em;
            color: $grey-500;

            .certificate {
                position: relative;
                margin-left: 12px;

                &::before {
                    position: absolute;
                    top: calc(50% - 4.5px);
                    left: -11px;
                    width: 9px;
                    height: 9px;
                    content: '';
                    border-radius: 50%;
                    border: 1px solid $grey-500;
                }
            }

            .button-wrapper {
                width: 100%;
                display: flex;
                justify-content: flex-end;
            }
        }
    }
}
</style>
